import { AlbumContentType } from 'common/enums';
import { adaptContentType } from 'helpers/utils';
import { AlbumContentSerialized } from 'networking/types/album-detail';

// Every clip gets a content row, including interview prompts that have not
// been recorded yet. Those show up as "video" contents with no media, so
// anything that lists memories has to leave them out; only the album planner
// wants them.
export const isPromptContent = (content: AlbumContentSerialized) => {
  const type = adaptContentType(content);
  if (type !== AlbumContentType.video && type !== AlbumContentType.audio) {
    return false;
  }
  const recordingStatus = content.content?.recordingStatus;
  if (recordingStatus) return recordingStatus !== 'recorded';
  return !content.mediaUrl;
};

// The album's contents that are actual memories (prompts excluded).
export const memoriesOf = (album: {
  contents?: AlbumContentSerialized[] | null;
}): AlbumContentSerialized[] =>
  (album.contents ?? []).filter((content) => !isPromptContent(content));

const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A memory date as a Date. Memory dates are calendar days ("2025-04-04");
 * `new Date()` reads those as midnight UTC, which is the previous evening
 * anywhere in the Americas, so they are built in local time instead.
 * Timestamps (e.g. createdAt) are parsed as usual.
 */
export const parseMemoryDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const day = DAY_ONLY.exec(value);
  const date = day
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The moment a memory is ordered by: the date set on it, else its file's own
 * date (an upload's or a note's), else when it was added. Same order as the
 * album page and the timeline.
 */
export const memorySortTime = (memory: {
  fileDate?: string | null;
  startDate?: string | null;
  createdAt?: string | null;
}): number =>
  parseMemoryDate(
    memory.startDate || memory.fileDate || memory.createdAt,
  )?.getTime() ?? 0;

/**
 * Printed QR codes that open a memory: the ones linked to it in the CMS, plus
 * a recording's codes linked in the mobile app (a CMS link to a recording is
 * both, hence the dedupe).
 */
export const memoryQrCodes = (memory: AlbumContentSerialized): QrCode[] => {
  const codes = [...(memory.qrCodes ?? []), ...(memory.content?.qrCodes ?? [])];
  return codes.filter(
    (code, index) => codes.findIndex((other) => other.id === code.id) === index,
  );
};
